import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Car, 
  ArrowLeft, 
  FileText, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Ban,
  Shield,
  CreditCard,
  Mail,
  Scale
} from "lucide-react";

const CGVPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Conditions Générales de Vente</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] mb-2">
              Conditions Générales de Vente
            </h1>
            <p className="text-slate-500">Dernière mise à jour : Février 2026</p>
          </div>

          {/* Présentation de l'entreprise */}
          <Card className="border-slate-200 bg-[#000091]/5">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#000091]" />
                Présentation
              </h2>
              <div className="text-slate-600 leading-relaxed space-y-2">
                <p>
                  <strong>cartegrise-etrangere.fr</strong> est une enseigne du groupe <strong>MOTEUR AND CO</strong>.
                </p>
                <p>
                  <strong>MOTEUR AND CO</strong><br />
                  6 rue d'Armaillé, 75017 Paris<br />
                  SIRET : 890 446 990 00016<br />
                  TVA : FR73890446990<br />
                  Email : contact@cartegrise-etrangere.fr
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 1 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#000091]" />
                Article 1 - Objet
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre cartegrise-etrangere.fr, 
                enseigne du groupe MOTEUR AND CO, service d'aide à l'immatriculation de véhicules importés, et ses clients. En utilisant nos services, 
                le client reconnaît avoir pris connaissance et accepté sans réserve les présentes CGV.
              </p>
            </CardContent>
          </Card>

          {/* Article 2 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#000091]" />
                Article 2 - Tarifs et modalités de paiement
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Nos prestations sont proposées aux tarifs suivants (TTC) :
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Immatriculation définitive :</strong> 99€ TTC</li>
                  <li><strong>W Provisoire + Immatriculation définitive :</strong> 149€ TTC</li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Frais supplémentaires</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Ces tarifs correspondent uniquement aux frais de dossier et de traitement. 
                        <strong> S'ajouteront obligatoirement :</strong>
                      </p>
                      <ul className="text-sm text-amber-700 mt-2 list-disc pl-4 space-y-1">
                        <li>La taxe régionale (variable selon la région et la puissance fiscale du véhicule)</li>
                        <li>La taxe de gestion et la redevance d'acheminement (environ 11€)</li>
                        <li>Le cas échéant, le malus écologique</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <p className="leading-relaxed">
                  Le paiement s'effectue en ligne par carte bancaire via notre prestataire de paiement sécurisé Stripe. 
                  La prestation débute dès réception du paiement et validation du dossier complet.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 3 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#000091]" />
                Article 3 - Délais de traitement
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Les délais de traitement dépendent de plusieurs facteurs :
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Traitement par nos services :</strong> 24 à 48h ouvrées après réception d'un dossier complet
                  </li>
                  <li>
                    <strong>Délivrance de la carte grise définitive :</strong> Variable selon les régions et la charge des services instructeurs de l'ANTS
                  </li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Délais indicatifs par région</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Selon la région de résidence du demandeur, le délai de traitement des dossiers définitifs par les services de l'État 
                        <strong> varie de 1 à 5 semaines</strong>. Ces délais sont indépendants de notre volonté et dépendent 
                        exclusivement de la charge de travail des services préfectoraux.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Article 4 - IMPORTANT */}
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-600" />
                Article 4 - Annulation et remboursement
              </h2>
              <div className="space-y-4 text-slate-700">
                <div className="bg-white border border-red-200 rounded-lg p-4">
                  <p className="font-semibold text-red-800 mb-2">4.1 - Dossier validé et transmis</p>
                  <p className="text-sm leading-relaxed">
                    Tout dossier validé par nos services et transmis au service instructeur (ANTS/Préfecture) peut être annulé à la demande du client. 
                    Cependant, <strong>les frais de dossier ne peuvent pas être remboursés</strong> car la prestation de vérification, 
                    de mise en forme et de transmission a déjà été effectuée.
                  </p>
                </div>

                <div className="bg-white border border-red-200 rounded-lg p-4">
                  <p className="font-semibold text-red-800 mb-2">4.2 - Refus par les services de l'État</p>
                  <p className="text-sm leading-relaxed">
                    Si le dossier est refusé par les services de l'État pour l'une des raisons suivantes, 
                    <strong> les frais de dossier ne seront pas remboursés :</strong>
                  </p>
                  <ul className="text-sm mt-2 list-disc pl-4 space-y-1">
                    <li>Véhicule déclaré volé (fichier des véhicules volés - FVV)</li>
                    <li>Véhicule accidenté et déclaré économiquement irréparable (VEI)</li>
                    <li>Véhicule gagé ou faisant l'objet d'une opposition</li>
                    <li>Véhicule faisant l'objet d'un procès-verbal de destruction</li>
                    <li>Non-conformité du véhicule aux normes françaises</li>
                    <li>Faux documents ou informations frauduleuses</li>
                    <li>Tout autre motif de refus imputable au véhicule ou au demandeur</li>
                  </ul>
                </div>

                <div className="bg-white border border-red-200 rounded-lg p-4">
                  <p className="font-semibold text-red-800 mb-2">4.3 - Rétractation avant transmission</p>
                  <p className="text-sm leading-relaxed">
                    Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne peut être exercé 
                    pour les contrats de prestations de services pleinement exécutés avant la fin du délai de rétractation. 
                    Si le dossier n'a pas encore été transmis aux services instructeurs, le client peut demander l'annulation 
                    et le remboursement intégral dans un délai de 14 jours.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Article 5 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#000091]" />
                Article 5 - Documents et pièces justificatives
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Le client s'engage à fournir des documents authentiques, lisibles et en cours de validité.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Délais supplémentaires</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Tout document manquant, illisible, périmé ou toute demande d'information complémentaire par les services de l'État 
                        <strong> entraînera un délai supplémentaire</strong> dans le traitement du dossier. 
                        Le dossier restera en attente jusqu'à la transmission du document demandé par le client.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="leading-relaxed">
                  Documents acceptés :
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Pièce d'identité en cours de validité (CNI, passeport)</li>
                  <li>Justificatif de domicile de moins de 6 mois (facture EDF, téléphone, avis d'imposition...)</li>
                  <li>Carte grise étrangère originale ou copie lisible</li>
                  <li>Quitus fiscal délivré par les services fiscaux</li>
                  <li>Contrôle technique français de moins de 6 mois (si véhicule de plus de 4 ans)</li>
                  <li>Attestation d'assurance en cours de validité</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Article 6 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#000091]" />
                Article 6 - Responsabilité
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Carte Grise Étrangère s'engage à traiter les dossiers avec diligence et professionnalisme. 
                  Notre responsabilité se limite à la vérification, la mise en forme et la transmission des dossiers aux services compétents.
                </p>
                <p className="leading-relaxed">
                  Nous ne pouvons être tenus responsables :
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Des délais de traitement des services de l'État (ANTS, Préfectures)</li>
                  <li>Des refus de dossiers pour des motifs liés au véhicule ou au demandeur</li>
                  <li>Des informations erronées fournies par le client</li>
                  <li>Des dysfonctionnements des systèmes informatiques de l'administration</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Article 7 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#000091]" />
                Article 7 - Réclamations
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Pour toute réclamation, le client peut nous contacter par email. 
                  Nous nous engageons à traiter les réclamations dans un délai de 48h ouvrées.
                </p>
                <p className="leading-relaxed">
                  En cas de litige persistant, le client peut recourir à un médiateur de la consommation conformément 
                  aux articles L.612-1 et suivants du Code de la consommation.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Article 8 */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#000091]" />
                Article 8 - Droit applicable
              </h2>
              <div className="space-y-4 text-slate-600">
                <p className="leading-relaxed">
                  Les présentes CGV sont soumises au droit français. En cas de litige, et après échec de toute tentative 
                  de recherche d'une solution amiable, les tribunaux français seront seuls compétents.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Back button */}
          <div className="text-center pt-4">
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="border-[#000091] text-[#000091]"
            >
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CGVPage;
